/**
 * App <-> n8n contract types. Source of truth: .internal/generator-app-n8n-spec.md (v1.0).
 * "Contract" here = API contract (payload shapes), not the customer-facing legal contract.
 *
 * Product-line generic: `productLine` is "generator" today; batteries / EV chargers
 * reuse this contract with a different productLine + package shape.
 */
export const SCHEMA_VERSION = "1.0";
/**
 * Cloud Functions deploy region — SINGLE SOURCE OF TRUTH. Both the functions
 * (deploy region) and the web client (`getFunctions(region)`) import this, so
 * they can never drift out of sync. Change it here and rebuild; both follow.
 *
 * NOTE: this is deliberately NOT the Vertex AI location — Gemini stays in
 * us-central1 for model availability (functions/src/config.ts VERTEX_LOCATION).
 */
export const FUNCTIONS_REGION = "us-east4";
/**
 * Submit-handoff timing — SINGLE SOURCE OF TRUTH shared by the web client (sets
 * the callable timeout) and the `submit` function (bounds its n8n retry budget).
 *
 * #14: the client aborts a callable at SUBMIT_CLIENT_TIMEOUT_MS while the server
 * retries n8n. If the server can run longer than the client waits, the customer
 * sees a failure for a contract that actually went out. These constants keep the
 * server's worst-case strictly under the client timeout; submitTiming.test.ts
 * enforces the invariant so they can never silently invert.
 */
export const SUBMIT_CLIENT_TIMEOUT_MS = 70_000;
/** Per-attempt fetch timeout for the WH-1 POST (AbortController in postSubmission). */
export const SUBMIT_ATTEMPT_TIMEOUT_MS = 12_000;
/** Backoff before each retry. Length + 1 = total attempts (here: 3). */
export const SUBMIT_RETRY_BACKOFF_MS = [2_000, 5_000];
/**
 * Headroom for the non-retry work in `submit` (Firestore read/writes, pricing
 * load, signed-URL minting) that also runs inside the client's timeout window.
 */
export const SUBMIT_REQUEST_OVERHEAD_MS = 15_000;
/** Worst-case server wall-clock for the handoff: every attempt hangs to its
 * timeout and every backoff is paid. Must stay under SUBMIT_CLIENT_TIMEOUT_MS. */
export function submitServerWorstCaseMs() {
    const attempts = SUBMIT_RETRY_BACKOFF_MS.length + 1;
    const backoff = SUBMIT_RETRY_BACKOFF_MS.reduce((a, b) => a + b, 0);
    return backoff + attempts * SUBMIT_ATTEMPT_TIMEOUT_MS;
}
